import { Circle, LogOut, Play, Square, Video } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AvatarVideoOverlay } from '@/components/video/AvatarVideoOverlay';
import { cn } from '@/lib/utils';

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

interface Prompt {
  promptId: string;
  text: string;
  maxDurationSec: number;
  order: number;
  videoPlaybackUrl?: string | null;
  videoDurationSec?: number | null;
}

interface Recording {
  promptId: string;
  blob: Blob;
  durationSec: number;
}

interface PromptRecorderProps {
  prompts: Prompt[];
  retakePolicy: 'unlimited' | 'once' | 'none';
  onComplete: (recordings: Recording[]) => void;
  onExit: () => void;
}

type RecordingPhase = 'idle' | 'avatar' | 'countdown' | 'recording' | 'review';

export function PromptRecorder({ prompts, retakePolicy, onComplete, onExit }: PromptRecorderProps) {
  const { t } = useTranslation();

  // Prompt navigation
  const [currentIndex, setCurrentIndex] = useState(0);
  const [recordings, setRecordings] = useState<Map<string, Recording>>(new Map());
  const [retakeCounts, setRetakeCounts] = useState<Map<string, number>>(new Map());

  // Recording state
  const [phase, setPhase] = useState<RecordingPhase>('idle');
  const [countdownValue, setCountdownValue] = useState(3);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [currentBlob, setCurrentBlob] = useState<Blob | null>(null);
  const [cameraError, setCameraError] = useState(false);

  // Refs
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const liveVideoRef = useRef<HTMLVideoElement>(null);
  const playbackVideoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recordingStartTimeRef = useRef<number>(0);

  const currentPrompt = prompts[currentIndex];
  const totalPrompts = prompts.length;
  const isLastPrompt = currentIndex === totalPrompts - 1;
  const hasAvatarVideo = !!currentPrompt?.videoPlaybackUrl;
  const hasText = !!currentPrompt?.text?.trim();

  // Initialize camera stream once
  useEffect(() => {
    let cancelled = false;

    async function initStream() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (liveVideoRef.current) {
          liveVideoRef.current.srcObject = stream;
        }
      } catch {
        if (!cancelled) {
          setCameraError(true);
        }
      }
    }

    void initStream();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      if (autoStopRef.current) clearTimeout(autoStopRef.current);
    };
  }, []);

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
    clearTimers();
  }, [clearTimers]);

  const startRecording = useCallback(() => {
    if (!streamRef.current || !currentPrompt) return;

    chunksRef.current = [];
    setElapsedSec(0);
    setPhase('recording');

    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';

    const recorder = new MediaRecorder(streamRef.current, { mimeType });
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const durationSec = Math.round((Date.now() - recordingStartTimeRef.current) / 1000);
      setCurrentBlob(blob);
      setElapsedSec(durationSec);
      setPhase('review');
      clearTimers();
    };

    recorder.start(1000);
    recordingStartTimeRef.current = Date.now();

    timerRef.current = setInterval(() => {
      const elapsed = Math.round((Date.now() - recordingStartTimeRef.current) / 1000);
      setElapsedSec(elapsed);
    }, 500);

    autoStopRef.current = setTimeout(() => {
      stopRecording();
    }, currentPrompt.maxDurationSec * 1000);
  }, [currentPrompt, clearTimers, stopRecording]);

  const startCountdown = useCallback(() => {
    if (!currentPrompt) return;
    setPhase('countdown');
    setCountdownValue(3);

    let count = 3;
    countdownTimerRef.current = setInterval(() => {
      count -= 1;
      if (count <= 0) {
        if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
        startRecording();
      } else {
        setCountdownValue(count);
      }
    }, 1000);
  }, [currentPrompt, startRecording]);

  const handleAvatarEnded = useCallback(() => {
    startCountdown();
  }, [startCountdown]);

  const handleStartClick = useCallback(() => {
    if (!currentPrompt) return;

    if (hasAvatarVideo) {
      setPhase('avatar');
    } else {
      startCountdown();
    }
  }, [currentPrompt, hasAvatarVideo, startCountdown]);

  const handleRetake = useCallback(() => {
    if (!currentPrompt) return;
    const promptId = currentPrompt.promptId;
    setRetakeCounts((prev) => {
      const next = new Map(prev);
      next.set(promptId, (prev.get(promptId) ?? 0) + 1);
      return next;
    });
    setCurrentBlob(null);
    setPhase('idle');
    setElapsedSec(0);
  }, [currentPrompt]);

  const handleNext = useCallback(() => {
    if (!currentPrompt || !currentBlob) return;
    const promptId = currentPrompt.promptId;

    const updatedRecordings = new Map(recordings);
    updatedRecordings.set(promptId, {
      promptId,
      blob: currentBlob,
      durationSec: elapsedSec,
    });
    setRecordings(updatedRecordings);

    if (isLastPrompt) {
      onComplete(Array.from(updatedRecordings.values()));
      return;
    }

    setCurrentIndex((prev) => prev + 1);
    setCurrentBlob(null);
    setPhase('idle');
    setElapsedSec(0);
  }, [currentPrompt, currentBlob, recordings, elapsedSec, isLastPrompt, onComplete]);

  const handleExit = useCallback(() => {
    const hasRecordings = recordings.size > 0 || currentBlob;
    if (hasRecordings) {
      const confirmed = window.confirm(t('video.recording.exitConfirm'));
      if (!confirmed) return;
    }
    stopRecording();
    onExit();
  }, [recordings, currentBlob, stopRecording, onExit, t]);

  // Set playback video src when blob changes
  useEffect(() => {
    if (currentBlob && playbackVideoRef.current) {
      const url = URL.createObjectURL(currentBlob);
      playbackVideoRef.current.src = url;
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    return undefined;
  }, [currentBlob]);

  const canRetake = (() => {
    if (!currentPrompt) return false;
    if (retakePolicy === 'none') return false;
    if (retakePolicy === 'unlimited') return true;
    const used = retakeCounts.get(currentPrompt.promptId) ?? 0;
    return used < 1;
  })();

  if (cameraError) {
    return (
      <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8'>
        <Video size={48} className='text-gray-400 mb-4' />
        <p className='text-gray-600 mb-4'>{t('video.recording.cameraError')}</p>
        <button
          type='button'
          onClick={onExit}
          className='px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors'>
          {t('video.recording.exit')}
        </button>
      </div>
    );
  }

  if (!currentPrompt) return null;

  return (
    <div className='flex flex-col h-full bg-gray-950 md:rounded-xl overflow-hidden'>
      {/* Progress bar */}
      <div className='flex gap-1 p-3'>
        {prompts.map((p, i) => (
          <div
            key={p.promptId}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors',
              i < currentIndex ? 'bg-green-500' : i === currentIndex ? 'bg-white' : 'bg-gray-700',
            )}
          />
        ))}
      </div>

      {/* Prompt text */}
      {hasText ? (
        <div className='px-4 py-3 md:px-6 md:py-4'>
          <p className='text-gray-400 text-xs md:text-sm mb-1'>
            {t('video.recording.promptLabel', { current: currentIndex + 1, total: totalPrompts })}
          </p>
          <p className='text-white text-base md:text-lg font-medium'>{currentPrompt.text}</p>
        </div>
      ) : null}

      {/* Video area — fills remaining space on mobile, aspect-video on desktop */}
      <div className='relative flex-1 bg-black mx-0 md:mx-4 md:flex-none md:aspect-video md:rounded-lg overflow-hidden'>
        <video
          ref={liveVideoRef}
          autoPlay
          muted
          playsInline
          className={cn('w-full h-full object-cover', phase === 'review' && 'hidden')}
        />

        {phase === 'review' ? (
          <video ref={playbackVideoRef} controls playsInline className='w-full h-full object-cover' />
        ) : null}

        {/* Avatar video overlay */}
        {phase === 'avatar' && currentPrompt.videoPlaybackUrl ? (
          <AvatarVideoOverlay src={currentPrompt.videoPlaybackUrl} onEnded={handleAvatarEnded} />
        ) : null}

        {phase === 'countdown' ? (
          <div className='absolute inset-0 flex flex-col items-center justify-center bg-black/60'>
            <span className='text-base md:text-lg text-white/70 mb-3'>{t('video.recording.recordingStartsIn')}</span>
            <span className='text-7xl md:text-8xl font-bold text-white animate-pulse'>{countdownValue}</span>
          </div>
        ) : null}

        {phase === 'recording' ? (
          <div className='absolute top-3 left-3 md:top-4 md:left-4 flex items-center gap-2'>
            <Circle size={12} className='text-red-500 fill-red-500 animate-pulse' />
            <span className='text-white text-xs md:text-sm font-medium'>{t('video.recording.recording')}</span>
          </div>
        ) : null}

        {phase === 'recording' ? (
          <div className='absolute top-3 right-3 md:top-4 md:right-4 bg-black/60 px-2 py-0.5 md:px-3 md:py-1 rounded-full'>
            <span className='text-white text-xs md:text-sm font-mono'>
              {formatTime(elapsedSec)} / {formatTime(currentPrompt.maxDurationSec)}
            </span>
          </div>
        ) : null}
      </div>

      {/* Controls */}
      <div className='p-4 md:p-6 flex items-center justify-between'>
        <button
          type='button'
          onClick={handleExit}
          className='flex items-center text-sm text-gray-400 hover:text-gray-200 transition-colors'>
          <LogOut size={16} className='mr-1' />
          {t('video.recording.exit')}
        </button>

        <div className='flex items-center gap-3'>
          {phase === 'idle' ? (
            <button
              type='button'
              onClick={handleStartClick}
              className='flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors text-sm md:text-base'>
              <Play size={18} className='fill-white' />
              {hasAvatarVideo ? t('video.recording.startInterview') : t('video.recording.startRecording')}
            </button>
          ) : null}

          {phase === 'avatar' ? (
            <span className='text-sm text-gray-400'>{t('video.recording.watchingAvatar')}</span>
          ) : null}

          {phase === 'recording' ? (
            <button
              type='button'
              onClick={stopRecording}
              className='flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors text-sm md:text-base'>
              <Square size={18} className='fill-white' />
              {t('video.recording.stopRecording')}
            </button>
          ) : null}

          {phase === 'review' ? (
            <>
              {canRetake ? (
                <button
                  type='button'
                  onClick={handleRetake}
                  className='px-4 py-2 md:px-5 md:py-2.5 border border-gray-600 text-gray-300 hover:text-white hover:border-gray-400 rounded-lg font-medium text-sm transition-colors'>
                  {t('video.recording.retake')}
                </button>
              ) : retakePolicy !== 'none' ? (
                <span className='text-gray-500 text-sm'>
                  {t('video.recording.retakesUsed', {
                    used: retakeCounts.get(currentPrompt.promptId) ?? 0,
                    max: retakePolicy === 'once' ? 1 : 0,
                  })}
                </span>
              ) : null}
              <button
                type='button'
                onClick={handleNext}
                className='px-5 py-2 md:px-6 md:py-2.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors text-sm md:text-base'>
                {isLastPrompt ? t('video.recording.finish') : t('video.recording.next')}
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
